import { type ActionEvent, Controller } from "@hotwired/stimulus";
import { effect } from "@preact/signals-core";
import { DIAGONAL } from "../maps/diagonal";
import { EMPTY } from "../maps/terrain";
import { initials, RING_COLORS, RING_INKS } from "../maps/tokens";
import type MapBoard from "./map_board_controller";
import { closeOtherPanels } from "./map_panels";

const DRAG_SLOP = 6;
const SEARCH_DELAY_MS = 250;
// Open from the start only where it leaves the map room: not on a phone held
// sideways, which is wide but short.
// Below it the drawing menu and the note card are sheets that cover the panel (`max-sm:` in _tray_panel).
const SHEET = "(max-width: 639px)";
const ROOMY = "(min-width: 640px) and (min-height: 500px), (min-width: 640px) and (pointer: fine)";
const fold = (text: string) => text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
const EDGE_HINTS = { p: "door", j: "window", e: "arrow_slit", g: "grate", m: "wall", ".": "eraser" } as const;

type Panel = "tokens" | "objects" | "board" | "fog" | "notes";
type Labels = Record<"hint" | "placing" | "painting" | "paintingBorder" | "erasing" | "diagonal" | "marker" | "board", string> & {
	notes: Record<"hint" | "armed", string>;
	edges: Record<"door" | "window" | "arrow_slit" | "grate" | "wall" | "eraser", string>;
	drawing: Record<string, string>;
	fog: Record<"reveal" | "hide" | "off", string>;
};
type FogChoices = Parameters<MapBoard["armFog"]>[0];
type FogState = { on: boolean; enabled: boolean; mode: string; shape: string };
type DrawChoices = Parameters<MapBoard["armDraw"]>[0];
type DrawState = {
	on: boolean;
	mode: string;
	preset: string;
	color: string;
	size: string;
	style: string;
	label: string;
	eraser: boolean;
	move: boolean;
	snap: boolean;
};

// Connects to data-controller="map-tray", beside map-board on the master's
// board root. Characters, retainers, monsters, markers and the Objetos
// panel's places go onto the map by dragging an item onto the board, or by
// tapping it and then the board. Enter on an item places it at the centre of
// the view. The Tabuleiro panel's terrain swatches arm the board's brush
// instead, its door, secret door, false door, grate and wall swatches the
// board's edge tool, and its Desenho chip's choices the board's pen, and
// Névoa's its fog tool. Peças, Objetos, Tabuleiro and Névoa are sibling
// panels; the pen is up exactly while Tabuleiro shows Desenho, and the fog
// tool while Névoa is open, with fog on.
export default class extends Controller {
	static targets = [
		"panel",
		"toggle",
		"item",
		"hint",
		"search",
		"results",
		"noResults",
		"markerLabel",
		"markerColor",
		"markerPreview",
		"resultTemplate",
		"collection",
		"collectionChoice",
		"gridKind",
		"objectsToggle",
		"swatch",
		"tool",
		"border",
		"objects",
		"objectFilter",
		"objectSection",
		"objectEmpty",
		"edgeSwatch",
		"drawChoice",
		"drawPreset",
		"drawColor",
		"drawSize",
		"drawStyle",
		"drawLabel",
		"drawEraser",
		"drawMove",
		"drawSnap",
		"fogEnabled",
		"fogChoice",
		"fogControl",
	];
	static values = { searchUrl: String, labels: Object };
	declare readonly panelTargets: HTMLElement[];
	declare readonly toggleTargets: HTMLElement[];
	declare readonly itemTargets: HTMLElement[];
	declare readonly hintTargets: HTMLElement[];
	declare readonly searchTarget: HTMLInputElement;
	declare readonly resultsTarget: HTMLElement;
	declare readonly noResultsTarget: HTMLElement;
	declare readonly markerLabelTarget: HTMLInputElement;
	declare readonly markerColorTargets: HTMLInputElement[];
	declare readonly markerPreviewTarget: HTMLElement;
	declare readonly hasMarkerPreviewTarget: boolean;
	declare readonly resultTemplateTarget: HTMLTemplateElement;
	declare readonly collectionTargets: HTMLElement[];
	declare readonly collectionChoiceTargets: HTMLInputElement[];
	declare readonly gridKindTargets: HTMLElement[];
	declare readonly objectsToggleTargets: HTMLElement[];
	declare readonly swatchTargets: HTMLElement[];
	declare readonly toolTargets: HTMLButtonElement[];
	declare readonly borderTargets: HTMLButtonElement[];
	declare readonly objectsTargets: HTMLElement[];
	declare readonly objectFilterTarget: HTMLInputElement;
	declare readonly hasObjectFilterTarget: boolean;
	declare readonly objectSectionTargets: HTMLElement[];
	declare readonly objectEmptyTarget: HTMLElement;
	declare readonly edgeSwatchTargets: HTMLElement[];
	declare readonly drawChoiceTargets: HTMLInputElement[];
	declare readonly drawPresetTarget: HTMLElement;
	declare readonly drawColorTarget: HTMLElement;
	declare readonly drawSizeTarget: HTMLElement;
	declare readonly drawStyleTarget: HTMLElement;
	declare readonly drawLabelTarget: HTMLElement;
	declare readonly drawEraserTarget: HTMLElement;
	declare readonly drawMoveTarget: HTMLElement;
	declare readonly drawSnapTarget: HTMLElement;
	declare readonly fogEnabledTarget: HTMLElement;
	declare readonly hasFogEnabledTarget: boolean;
	declare readonly fogChoiceTargets: HTMLInputElement[];
	declare readonly fogControlTargets: (HTMLInputElement | HTMLButtonElement)[];
	declare readonly searchUrlValue: string;
	declare readonly labelsValue: Labels;
	open: Panel | null = null;
	tab?: string;
	brush?: string | null;
	brushName?: string | null;
	bordered = false;
	tool = "brush";
	border = ".";
	edge?: string | null;
	drawing?: DrawState;
	fog?: FogState;
	notesArmed = false;
	armed?: HTMLElement | null;
	searched = false;
	request?: AbortController;
	searchTimer?: ReturnType<typeof setTimeout>;
	dragged = false;
	drag?: { item: HTMLElement; start: { x: number; y: number }; ghost?: HTMLElement } | null;
	sheetObserver?: MutationObserver;
	phone?: MediaQueryList;
	onPhone?: () => void;
	disposePlaced?: () => void;
	disposeTerrain?: () => void;
	disposeFog?: () => void;
	disposeStyle?: () => void;

	connect() {
		this.previewMarker();
		if (window.matchMedia(ROOMY).matches) this.show();
		this.watchSheets();
		if (this.board?.renderer) this.ready();
	}

	// The faint grid follows whether the Tabuleiro panel is on screen, which a sheet or the breakpoint can change without a close().
	watchSheets() {
		this.sheetObserver = new MutationObserver(() => this.syncFloorGrid());
		this.sheetObserver.observe(this.element, { attributes: true, attributeFilter: ["data-drawing-menu", "data-note-card"] });
		this.phone = window.matchMedia(SHEET);
		this.onPhone = () => this.syncFloorGrid();
		this.phone.addEventListener("change", this.onPhone);
		this.syncFloorGrid();
	}

	syncFloorGrid() {
		const covered = window.matchMedia(SHEET).matches && (this.element.hasAttribute("data-drawing-menu") || this.element.hasAttribute("data-note-card"));
		this.board?.showFloorGrid(this.open === "board" && !covered);
	}

	disconnect() {
		this.sheetObserver?.disconnect();
		if (this.onPhone) this.phone?.removeEventListener("change", this.onPhone);
		this.disposePlaced?.();
		this.disposeTerrain?.();
		this.disposeFog?.();
		this.disposeStyle?.();
		this.stopDrag();
		clearTimeout(this.searchTimer);
		this.request?.abort();
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	ready() {
		const { state } = this.board;
		this.syncFloorGrid();
		this.disposePlaced?.();
		this.disposePlaced = effect(() => {
			const tokens = Object.values(state.view.value.tokens ?? {});
			const placed = new Set(tokens.flatMap((token: Record<string, string>) => [token.character_id, token.retainer_id]).filter(Boolean));
			for (const item of this.itemTargets) {
				const subject = this.subject(item);
				const mark = item.querySelector<HTMLElement>("[data-placed-mark]");
				if (mark) mark.hidden = !placed.has(subject.character_id ?? subject.retainer_id);
			}
		});
		this.disposeTerrain?.();
		let shown = false;
		this.disposeTerrain = effect(() => {
			const kind = state.view.value.scene?.grid?.kind;
			if (!kind || shown) return;
			shown = true;
			this.showGrid(kind);
		});
		this.disposeStyle?.();
		this.disposeStyle = effect(() => this.showTerrainStyle(state.view.value.scene?.terrain_style));
		this.disposeFog?.();
		this.disposeFog = effect(() => this.showFogEnabled(Boolean(state.view.value.scene?.fog?.enabled)));
		if (!this.searched) this.search();
	}

	// Each chip, each drawing style and each Objetos section carries its grid
	// kind and shows only on scenes of it, the chips in the tray's order; the
	// Desenho chip carries none and shows on both. The Objetos button and panel
	// read Lugares on a hex scene. The board opens on the first chip shown.
	showGrid(kind: string) {
		for (const element of this.gridKindTargets) element.hidden = element.dataset.grid !== kind;
		for (const button of this.objectsToggleTargets) {
			const label = kind === "hex" ? button.dataset.hexLabel : button.dataset.squareLabel;
			button.title = label;
			button.setAttribute("aria-label", label);
		}
		this.showPresets(kind);
		if (this.hasObjectFilterTarget) this.filterObjects();
		const chip = (choice: HTMLInputElement) => choice.closest("label") as HTMLElement;
		for (const choice of this.collectionChoiceTargets) chip(choice).hidden = Boolean(choice.dataset.grid) && choice.dataset.grid !== kind;
		const first = this.collectionChoiceTargets.find((choice) => !chip(choice).hidden);
		if (!first) return;
		for (const choice of this.collectionChoiceTargets) choice.checked = choice === first;
		this.disarm();
		this.board?.armBrush(null);
		this.board?.armEdge(null);
		this.showCollection(first.value);
	}

	// An armed piece the filter hides is put down: a tap on the map must not place what the list no longer shows.
	filterObjects() {
		const query = fold(this.objectFilterTarget.value.trim());
		let found = false;
		for (const section of this.objectSectionTargets) {
			let any = false;
			for (const piece of section.querySelectorAll<HTMLElement>("li")) {
				piece.hidden = !fold(piece.textContent ?? "").includes(query);
				any ||= !piece.hidden;
			}
			section.hidden = !any;
			found ||= any && !section.parentElement?.hidden;
		}
		this.objectEmptyTarget.hidden = found;
		if (this.armed?.closest("li")?.hidden) this.disarm();
	}

	// The map's style is the scene's: the square swatches' art and
	// the Objetos thumbnails follow it when another tab or another master switches it.
	showTerrainStyle(style: string | undefined) {
		if (!style) return;
		for (const group of [...this.collectionTargets, ...this.objectsTargets]) group.dataset.terrainStyle = style;
	}

	showCollection(key: string) {
		this.tab = key;
		for (const group of this.collectionTargets) group.hidden = group.dataset.collection !== key;
	}

	// What the swatch paints with; the board keeps it, so it holds from one
	// swatch to the next.
	pickTool({ currentTarget }: Event & { currentTarget: HTMLElement }) {
		const { tool } = currentTarget.dataset;
		this.tool = tool as string;
		this.showTools();
		this.board?.setPaintTool(tool as Parameters<MapBoard["setPaintTool"]>[0]);
	}

	// Diagonal is always the brush: the other two tools show disabled until another chip is armed.
	showTools() {
		const diagonal = this.brush === DIAGONAL;
		for (const button of this.toolTargets) {
			button.setAttribute("aria-pressed", String(button.dataset.tool === (diagonal ? "brush" : this.tool)));
			button.disabled = diagonal && button.dataset.tool !== "brush";
		}
	}

	pickBorder({ currentTarget }: Event & { currentTarget: HTMLElement }) {
		this.border = currentTarget.dataset.border as string;
		this.showBorders();
		this.board?.setPaintBorder(this.border);
		this.showHint();
	}

	showBorders() {
		const none = this.brush === DIAGONAL || this.brush === EMPTY;
		for (const button of this.borderTargets) {
			button.setAttribute("aria-pressed", String(button.dataset.border === this.border));
			button.disabled = none;
		}
	}

	// The armed swatch chosen again puts the brush down.
	pickBrush(event: Event & { currentTarget: HTMLElement }) {
		const { code } = event.currentTarget.dataset;
		this.board?.armBrush(code === this.brush ? null : code);
	}

	brushChanged({ detail: { code } }: CustomEvent<{ code: string | null }>) {
		this.brush = code;
		this.brushName = null;
		this.bordered = false;
		for (const swatch of this.swatchTargets) {
			swatch.setAttribute("aria-pressed", String(swatch.dataset.code === code));
			if (swatch.dataset.code === code) this.brushName = swatch.dataset.name;
			if (swatch.dataset.code === code) this.bordered = this.borderTargets.some((button) => button.closest("[data-collection]")?.contains(swatch));
		}
		this.showTools();
		this.showBorders();
		if (code) this.setArmed(null);
		else this.showHint();
	}

	// The armed edge swatch chosen again puts the edge tool down.
	pickEdge(event: Event & { currentTarget: HTMLElement }) {
		const { edge } = event.currentTarget.dataset;
		this.board?.armEdge(edge === this.edge ? null : edge);
	}

	edgeChanged({ detail: { kind } }: CustomEvent<{ kind: string | null }>) {
		this.edge = kind;
		for (const swatch of this.edgeSwatchTargets) swatch.setAttribute("aria-pressed", String(swatch.dataset.edge === kind));
		if (kind) this.setArmed(null);
		else this.showHint();
	}

	// A chip shows its collection's group and hides the rest, entirely in the
	// browser; what is armed stays in view, so switching puts it down. Desenho
	// picks the pen up, any other chip puts it down. The group is shown first, so
	// the pen going down is not read as Desenho closing.
	pickCollection({ target }: Event & { target: HTMLInputElement }) {
		this.choose(target.value);
		if (target.value !== "drawing") this.board?.setDraw(false);
		else if (!this.drawing?.on) this.board?.armDraw();
		this.showHint();
	}

	choose(key: string) {
		for (const choice of this.collectionChoiceTargets) choice.checked = choice.value === key;
		this.showCollection(key);
		this.disarm();
		this.board?.armBrush(null);
		this.board?.armEdge(null);
	}

	get penShowing() {
		return this.open === "board" && this.tab === "drawing";
	}

	// Any choice on the Desenho chip picks the pen up with it; only the
	// clicked choice is sent, the board keeps the rest.
	pickDraw({ currentTarget: input }: { currentTarget: HTMLInputElement }) {
		this.board?.armDraw({ [input.dataset.choice]: input.value, eraser: false, move: false } as DrawChoices);
	}

	toggleDrawEraser() {
		this.board?.armDraw({ eraser: !(this.drawing?.on && this.drawing.eraser) });
	}

	toggleDrawMove() {
		this.board?.armDraw({ move: !(this.drawing?.on && this.drawing.move) });
	}

	toggleDrawSnap() {
		this.board?.armDraw({ snap: !this.drawing?.snap });
	}

	pickFog() {
		const choices: Record<string, string> = {};
		for (const input of this.fogChoiceTargets) if (input.checked) choices[input.dataset.choice] = input.value;
		this.board?.armFog(choices as FogChoices);
	}

	// The switch writes the fog on or off; turned on from the open panel, the
	// tool comes up with it.
	toggleFogEnabled() {
		const on = this.fogEnabledTarget.getAttribute("aria-pressed") !== "true";
		this.showFogEnabled(on);
		this.board?.setFogEnabled(on);
		if (on && this.open === "fog") this.board?.armFog();
	}

	showFogEnabled(enabled: boolean) {
		if (this.hasFogEnabledTarget) this.fogEnabledTarget.setAttribute("aria-pressed", String(enabled));
		for (const control of this.fogControlTargets) control.disabled = !enabled;
	}

	fogAll({ params: { state } }: Pick<ActionEvent, "params"> & { params: { state: "revealed" | "hidden" } }) {
		this.board?.fogAll(state);
	}

	// The note tool going up or down only changes what a tap on the map is said to do: the panel is the one that arms it.
	notesChanged({ detail: { on } }: Pick<CustomEvent<{ on: boolean }>, "detail">) {
		this.notesArmed = on;
		this.showHint();
	}

	// As drawChanged: the tool going up opens Névoa, going down closes it, but
	// fog turned off leaves the panel open on its switch.
	fogChanged({ detail }: Pick<CustomEvent<FogState>, "detail">) {
		this.fog = detail;
		for (const input of this.fogChoiceTargets) input.checked = detail[input.dataset.choice] === input.value;
		if (detail.on && this.open !== "fog") this.show("fog");
		else if (!detail.on && detail.enabled && this.open === "fog") this.close();
		else this.showHint();
	}

	// The board keeps the choices; the panel shows them, and opens on Desenho
	// when the pen is picked up from L, X, or another panel. Recording the
	// choices before showing or closing is what keeps show("board") from
	// A style is shown on scenes of its grid kind; the lines-only ones also not
	// while the mode is Área, since the server refuses them on an area.
	showPresets(kind: string | undefined) {
		const area = this.drawing?.mode === "area";
		for (const label of this.drawPresetTarget.querySelectorAll<HTMLElement>("label[data-grid]")) {
			label.hidden = label.dataset.grid !== kind || (area && "linesOnly" in label.dataset);
		}
	}

	// arming the pen again: it reads this.drawing, already up to date.
	drawChanged({ detail }: Pick<CustomEvent<DrawState>, "detail">) {
		this.drawing = detail;
		this.showPresets(this.board?.state?.view.peek().scene?.grid?.kind);
		for (const input of this.drawChoiceTargets) input.checked = detail[input.dataset.choice] === input.value;
		// Texto takes a colour and a size of its own; a line its style, or with
		// Livre a colour, a width and a stroke.
		const text = detail.mode === "text";
		const custom = !text && detail.preset === "livre";
		this.drawPresetTarget.hidden = text;
		this.drawColorTarget.hidden = !text && !custom;
		this.drawSizeTarget.hidden = !custom;
		this.drawStyleTarget.hidden = !custom;
		this.drawLabelTarget.hidden = !text;
		this.drawSnapTarget.hidden = text || detail.mode === "free";
		this.drawSnapTarget.setAttribute("aria-pressed", String(detail.snap));
		this.drawEraserTarget.setAttribute("aria-pressed", String(detail.on && detail.eraser));
		this.drawMoveTarget.setAttribute("aria-pressed", String(detail.on && detail.move));
		if (detail.on && !this.penShowing) {
			this.choose("drawing");
			this.show("board");
		} else if (!detail.on && this.penShowing) this.close();
		else this.showHint();
	}

	// What a tap on the board does now.
	showHint() {
		const labels = this.labelsValue;
		let hint = this.open === "board" ? labels.board : labels.hint;
		if (this.armed) hint = labels.placing.replace("%{name}", this.subject(this.armed).name);
		else if (this.brush === DIAGONAL) hint = labels.diagonal;
		else if (this.brush) hint = this.brushName ? this.paintingHint(labels) : labels.erasing;
		else if (this.edge) hint = labels.edges[EDGE_HINTS[this.edge]];
		else if (this.drawing?.on) hint = labels.drawing[this.drawing.move ? "move" : this.drawing.eraser ? "eraser" : this.drawing.mode];
		else if (this.open === "fog") hint = this.fog && !this.fog.enabled ? labels.fog.off : labels.fog[this.fog?.mode === "hide" ? "hide" : "reveal"];
		else if (this.open === "notes") hint = labels.notes[this.notesArmed ? "armed" : "hint"];
		for (const target of this.hintTargets) target.textContent = hint;
	}

	paintingHint(labels: Labels) {
		const border = this.borderTargets.find((button) => button.dataset.border === this.border)?.dataset.name;
		const hint = this.bordered && border ? labels.paintingBorder.replace("%{border}", border) : labels.painting;
		return hint.replace("%{name}", this.brushName as string);
	}

	toggle({ params: { panel } }: Pick<ActionEvent, "params"> & { params: { panel: Panel } }) {
		if (this.open === panel) this.close();
		else this.show(panel);
	}

	// One panel at a time, among Peças, Objetos, Tabuleiro and Névoa,
	// and the calibration and dice panels: opening one closes whichever other is open.
	// Leaving a panel puts down what it armed.
	show(panel: Panel = "tokens") {
		closeOtherPanels(this.application, this.element, "map-tray");
		if (this.open && this.open !== panel) {
			this.disarm();
			this.board?.armBrush(null);
			this.board?.armEdge(null);
			if (this.penShowing) this.board?.setDraw(false);
			if (this.open === "fog") this.board?.setFog(false);
			if (this.open === "notes") this.board?.setNotes(false);
		}
		this.open = panel;
		for (const target of this.panelTargets) target.hidden = target.dataset.panel !== panel;
		for (const toggle of this.toggleTargets) toggle.setAttribute("aria-pressed", String(toggle.dataset.mapTrayPanelParam === panel));
		if (panel === "tokens" && this.board?.state && !this.searched) this.search();
		if (panel === "board" && this.tab === "drawing" && !this.drawing?.on) this.board?.armDraw();
		if (panel === "fog" && !this.fog?.on) this.board?.armFog();
		this.syncFloorGrid();
		this.showHint();
	}

	close() {
		const panel = this.open;
		this.open = null;
		if (panel) {
			for (const target of this.panelTargets) if (target.dataset.panel === panel) target.hidden = true;
			for (const toggle of this.toggleTargets) if (toggle.dataset.mapTrayPanelParam === panel) toggle.setAttribute("aria-pressed", "false");
		}
		this.disarm();
		this.board?.armBrush(null);
		this.board?.armEdge(null);
		this.board?.setDraw(false);
		this.board?.setFog(false);
		this.board?.setNotes(false);
		this.syncFloorGrid();
	}

	subject(item: HTMLElement) {
		if (item.dataset.marker === undefined) return JSON.parse(item.dataset.token);
		const label = this.markerLabelTarget.value.trim() || this.labelsValue.marker;
		return { kind: "marker", label, name: label, ring_color: this.markerColor };
	}

	get markerColor() {
		return this.markerColorTargets.find((input) => input.checked)?.value ?? "azul";
	}

	previewMarker() {
		if (!this.hasMarkerPreviewTarget) return;
		this.markerPreviewTarget.style.backgroundColor = RING_COLORS[this.markerColor];
		this.markerPreviewTarget.style.color = RING_INKS[this.markerColor];
		this.markerPreviewTarget.textContent = initials(this.markerLabelTarget.value || this.labelsValue.marker);
	}

	// A click without a drag arms the item, so the next tap on the board places
	// it; from the keyboard (no pointer, detail 0) it goes to the view's centre.
	arm(event: MouseEvent & { currentTarget: HTMLElement }) {
		const item = event.currentTarget;
		if (this.dragged) return;
		const board = this.board;
		if (!board?.renderer) return;
		if (event.detail === 0) {
			const { cx, cy } = board.renderer.centre();
			board.placeToken(this.subject(item), { x: cx, y: cy });
			return;
		}
		if (this.armed === item) return this.disarm();
		board.arm(() => this.subject(item));
		this.setArmed(item);
	}

	disarm() {
		if (!this.armed) return;
		this.board?.disarm();
		this.setArmed(null);
	}

	placed() {
		this.setArmed(null);
	}

	setArmed(item: HTMLElement | null) {
		this.armed = item;
		for (const other of this.itemTargets) other.setAttribute("aria-pressed", String(other === item));
		this.showHint();
	}

	grab(event: PointerEvent & { currentTarget: HTMLElement }) {
		if (event.button !== 0 || event.pointerType === "touch") return;
		this.dragged = false;
		this.drag = { item: event.currentTarget, start: { x: event.clientX, y: event.clientY } };
		window.addEventListener("pointermove", this.dragMove);
		window.addEventListener("pointerup", this.dragEnd);
		window.addEventListener("pointercancel", this.stopDrag);
	}

	dragMove = (event: PointerEvent) => {
		const drag = this.drag;
		if (!drag) return;
		if (!drag.ghost) {
			if (Math.hypot(event.clientX - drag.start.x, event.clientY - drag.start.y) < DRAG_SLOP) return;
			drag.ghost = this.ghost(drag.item);
			this.dragged = true;
		}
		drag.ghost.style.transform = `translate(${event.clientX}px, ${event.clientY}px) translate(-50%, -50%)`;
	};

	dragEnd = (event: PointerEvent) => {
		const drag = this.drag;
		this.stopDrag();
		if (!drag?.ghost) return;
		const board = this.board;
		const target = document.elementFromPoint(event.clientX, event.clientY);
		const point = board?.canvasTarget.contains(target) ? board.renderer?.clientToImage(event.clientX, event.clientY) : null;
		if (point) board.placeToken(this.subject(drag.item), point);
		// The click that follows a drag must not arm the item as well.
		setTimeout(() => (this.dragged = false));
	};

	stopDrag = () => {
		this.drag?.ghost?.remove();
		this.drag = null;
		window.removeEventListener("pointermove", this.dragMove);
		window.removeEventListener("pointerup", this.dragEnd);
		window.removeEventListener("pointercancel", this.stopDrag);
	};

	ghost(item: HTMLElement) {
		const mono = item.closest<HTMLElement>("[data-terrain-style]")?.dataset.terrainStyle === "mono";
		const face = (mono && item.querySelector("[data-face][data-mono]")) || item.querySelector("[data-face]");
		const ghost = face.cloneNode(true) as HTMLElement;
		// The Preto & Branco thumbnail is hidden until its group shows it; the ghost has no group.
		ghost.classList.remove("hidden");
		ghost.classList.add("fixed", "top-0", "left-0", "z-50", "pointer-events-none", "shadow-lg", "opacity-90");
		ghost.setAttribute("aria-hidden", "true");
		document.body.append(ghost);
		return ghost;
	}

	// The tray's face_class, minus the portrait's own object-cover and bg-surface,
	// which would hide the concept thumb's color.
	fallbackFace(name: string) {
		const fallback = document.createElement("span");
		fallback.className = "w-9 h-9 shrink-0 rounded-full border-2 border-line flex items-center justify-center text-xs font-bold odo-concept-thumb";
		fallback.dataset.face = "";
		fallback.textContent = initials(name);
		return fallback;
	}

	queueSearch() {
		clearTimeout(this.searchTimer);
		this.searchTimer = setTimeout(() => this.search(), SEARCH_DELAY_MS);
	}

	async search() {
		this.searched = true;
		this.request?.abort();
		const request = new AbortController();
		this.request = request;
		const url = new URL(this.searchUrlValue, window.location.origin);
		url.searchParams.set("q", this.searchTarget.value.trim());
		try {
			const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin", signal: request.signal });
			if (!response.ok) return;
			const { monsters } = await response.json();
			this.showResults(monsters);
		} catch {}
	}

	showResults(monsters: { name: string; image_url?: string; custom?: boolean }[]) {
		const items = monsters.map((monster) => {
			const item = this.resultTemplateTarget.content.firstElementChild.cloneNode(true) as HTMLElement;
			item.querySelector("button").dataset.token = JSON.stringify(monster);
			const face = item.querySelector("img");
			if (monster.image_url) face.src = monster.image_url;
			else face.replaceWith(this.fallbackFace(monster.name));
			item.querySelector("[data-name]").textContent = monster.name;
			item.querySelector<HTMLElement>("[data-custom]").hidden = !monster.custom;
			return item;
		});
		this.resultsTarget.replaceChildren(...items);
		this.noResultsTarget.hidden = items.length > 0;
	}
}
